import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calendar, Clock, Video, Mic, Sparkles, Users, Ticket, ArrowRight, Tag,
  Check, Lock, Radio, LogIn, Link2,
} from 'lucide-react';
import VerifiedBadge from './VerifiedBadge';
import { mediaUrl } from '../api/posts';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { bookFreeLive, buyLiveTicket } from '../api/lives';
import { fmtCredits } from '../lib/currency';
import { useCategories } from '../api/categories';
import InsufficientBalanceDialog from './InsufficientBalanceDialog';

const fmtDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
};
const fmtTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const Avatar = ({ src, name, size = 36 }) => (
  <div className="shrink-0 overflow-hidden rounded-full bg-card" style={{ width: size, height: size }}>
    {src ? (
      <img src={src} alt={name} className="h-full w-full rounded-full object-cover object-center" />
    ) : (
      <div
        className="flex h-full w-full items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
      >
        {(name || '?').charAt(0).toUpperCase()}
      </div>
    )}
  </div>
);

const StatusPill = ({ status, t }) => {
  if (status === 'live') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-card" /> {t('card.live_badge')}
      </span>
    );
  }
  if (status === 'ended') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] font-semibold uppercase text-neutral-300">
        {t('card.ended_badge')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-blue-300">
      <Calendar className="h-3 w-3" /> {t('card.scheduled_badge')}
    </span>
  );
};

// Phase 27.2 — inline CTA shown directly on the card when actionable=true.
// Mirrors LiveDetailPage's BookingCtaCard logic but condensed for the
// profile listing context. Clicks stopPropagation so the surrounding card
// click (navigate to detail) doesn't fire.
const InlineCta = ({ live, busy, alreadyHas, isSoldOut, isLiveNow, onClick, t }) => {
  const stop = (fn) => (e) => { e.stopPropagation(); fn(e); };

  // Phase 27.3 — Live in corso → SEMPRE "Entra nella live", anche se
  // l'utente non ha ancora prenotato. LiveDetailPage gestirà eventuali
  // gate (ticket required, accesso negato).
  if (isLiveNow) {
    return (
      <Button
        onClick={stop(() => onClick('join'))}
        size="sm"
        className="h-8 rounded-full bg-red-500 px-3 text-xs font-semibold text-white hover:bg-red-600"
        data-testid={`live-card-enter-btn-${live.id}`}
      >
        <Radio className="mr-1 h-3.5 w-3.5" /> {t('card.enter_live')}
      </Button>
    );
  }
  if (alreadyHas) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-3 py-1 text-[11px] font-semibold text-white"
        data-testid={`live-card-booked-${live.id}`}
      >
        <Check className="h-3.5 w-3.5" /> {live.is_paid ? t('card.ticket_owned') : t('card.booked')}
      </span>
    );
  }
  if (isSoldOut) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-red-500/90 px-3 py-1 text-[11px] font-semibold text-white"
        data-testid={`live-card-soldout-btn-${live.id}`}
      >
        <Lock className="h-3.5 w-3.5" /> {t('card.sold_out')}
      </span>
    );
  }
  if (live.is_paid) {
    return (
      <Button
        onClick={stop(() => onClick('buy'))}
        disabled={busy}
        size="sm"
        className="gold-gradient h-8 rounded-full px-3 text-xs font-semibold text-black"
        data-testid={`live-card-buy-btn-${live.id}`}
      >
        <Ticket className="mr-1 h-3.5 w-3.5" />
        {busy ? '…' : t('card.buy_ticket_short', { price: fmtCredits(live.price || 0) })}
      </Button>
    );
  }
  return (
    <Button
      onClick={stop(() => onClick('book'))}
      disabled={busy}
      size="sm"
      className="gold-gradient h-8 rounded-full px-3 text-xs font-semibold text-black"
      data-testid={`live-card-book-btn-${live.id}`}
    >
      <Ticket className="mr-1 h-3.5 w-3.5" />
      {busy ? '…' : t('card.book_free')}
    </Button>
  );
};

const LiveCard = ({ live, compact = false, actionable = false, onChange }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('lives');
  const { nameOf } = useCategories();
  const [busy, setBusy] = React.useState(false);
  const [localLive, setLocalLive] = React.useState(live);
  const [insufficient, setInsufficient] = React.useState(false);
  React.useEffect(() => { setLocalLive(live); }, [live]);
  const KindIcon = localLive.kind === 'audio' ? Mic : Video;
  const initialCover = localLive.cover_url ? mediaUrl(localLive.cover_url) : '';
  const [coverFailed, setCoverFailed] = React.useState(false);
  const cover = coverFailed ? '' : initialCover;
  const cap = localLive.max_participants;
  const sold = Number(localLive.tickets_sold || 0);
  const seatsLeft = cap ? Math.max(0, Number(cap) - sold) : null;
  const isSoldOut = cap && seatsLeft === 0;
  const isLiveNow = localLive.status === 'live';
  const alreadyHas = localLive.is_paid ? !!localLive.has_ticket : !!localLive.is_booked;

  const kindLabel = (kind) => (kind === 'audio' ? t('card.kind_audio_only') : t('card.kind_video_audio'));

  const goToDetail = () => navigate(`/lives/${localLive.id}`);

  // NeoUI — private lives are invite-only: the owner can copy the direct
  // link from the card and share it with the people they want to invite.
  const isOwner = !!user && user.id === localLive.creator_id;
  const copyInviteLink = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const url = `${window.location.origin}/lives/${localLive.id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch (_err) {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    toast({ title: t('card.invite_copied_title'), description: t('card.invite_copied_desc') });
  };

  const handleAction = async (kind) => {
    if (!user) {
      toast({ title: t('card.login_to_book_toast_title'), description: t('card.login_to_book_toast_desc') });
      navigate('/login');
      return;
    }
    if (kind === 'join') {
      goToDetail();
      return;
    }
    if (kind === 'buy') {
      setBusy(true);
      try {
        const res = await buyLiveTicket(localLive.id);
        toast({
          title: t('card.ticket_bought_title'),
          description: t('card.ticket_bought_desc', { amount: fmtCredits(res?.purchase?.amount || localLive.price) }),
        });
        try { window.dispatchEvent(new CustomEvent('nymia:data-changed')); } catch (_) { /* noop */ }
        const updated = { ...localLive, has_ticket: true };
        setLocalLive(updated);
        if (typeof onChange === 'function') onChange(updated);
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (err.response?.status === 402 && detail.startsWith('insufficient_balance')) {
          setInsufficient(true);
        } else {
          toast({
            title: t('card.purchase_failed_title'),
            description: detail.split('|')[1] || detail || '',
            variant: 'destructive',
          });
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    if (kind === 'book') {
      setBusy(true);
      try {
        const updated = await bookFreeLive(localLive.id);
        setLocalLive(updated);
        toast({ title: t('card.booking_confirmed_title'), description: t('card.booking_confirmed_desc') });
        if (typeof onChange === 'function') onChange(updated);
        try { window.dispatchEvent(new CustomEvent('nymia:data-changed')); } catch (_) { /* noop */ }
      } catch (err) {
        toast({
          title: t('card.booking_failed_title'),
          description: err.response?.data?.detail || '',
          variant: 'destructive',
        });
      } finally {
        setBusy(false);
      }
    }
  };

  const cardBody = (
    <>
      {/* Cover / thumbnail */}
      <div
        className={`relative flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg ${
          cover
            ? 'bg-neutral-900'
            : isLiveNow
              ? 'bg-gradient-to-br from-red-500 to-rose-700 text-white'
              : localLive.kind === 'audio'
                ? 'bg-gradient-to-br from-amber-700 to-orange-800 text-amber-100'
                : 'bg-gradient-to-br from-violet-700 to-fuchsia-800 text-violet-100'
        }`}
      >
        {cover ? (
          <img
            src={cover}
            alt={localLive.title}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={() => setCoverFailed(true)}
          />
        ) : (
          <KindIcon className="h-9 w-9" strokeWidth={1.6} />
        )}
        <div className="absolute left-1.5 top-1.5">
          <StatusPill status={localLive.status} t={t} />
        </div>
        {localLive.is_paid ? (
          <div className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 rounded-full bg-amber-500/95 px-1.5 py-0.5 text-[10px] font-bold text-black backdrop-blur" data-testid={`live-card-paid-badge-${localLive.id}`}>
            <Sparkles className="h-3 w-3" /> {fmtCredits(localLive.price || 0)}
          </div>
        ) : (
          <div className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-1.5 py-0.5 text-[10px] font-bold text-white backdrop-blur" data-testid={`live-card-free-badge-${localLive.id}`}>
            {t('card.free_badge')}
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="flex min-w-0 flex-1 flex-col justify-between">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{localLive.title}</p>
          {!compact && localLive.description && (
            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{localLive.description}</p>
          )}
          <div className="mt-1 flex items-center gap-2">
            <Avatar src={localLive.creator?.avatar_url} name={localLive.creator?.username} size={20} />
            <span className="inline-flex items-center gap-1 truncate text-xs font-medium">
              @{localLive.creator?.username}
              {localLive.creator?.is_verified_creator && <VerifiedBadge size={12} />}
            </span>
          </div>

          {/* Chips: kind + category */}
          {!compact && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span
                className="inline-flex items-center gap-1 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-foreground/80"
                data-testid={`live-card-kind-${localLive.id}`}
              >
                <KindIcon className="h-3 w-3" /> {kindLabel(localLive.kind)}
              </span>
              {localLive.category && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-foreground/80"
                  data-testid={`live-card-category-${localLive.id}`}
                >
                  <Tag className="h-3 w-3" /> {nameOf(localLive.category) || localLive.category}
                </span>
              )}
              {localLive.privacy === 'private' && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-500"
                  data-testid={`live-card-private-badge-${localLive.id}`}
                >
                  <Lock className="h-3 w-3" /> {t('card.private_badge')}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {fmtDate(localLive.scheduled_at)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {fmtTime(localLive.scheduled_at)} · {localLive.duration_minutes}m
          </span>
          {isLiveNow ? (
            <span className="inline-flex items-center gap-1 font-semibold text-red-600">
              <Users className="h-3 w-3" /> {t('card.watching_now', { count: localLive.viewers_count })}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1" data-testid={`live-card-bookings-${localLive.id}`}>
              <Ticket className="h-3 w-3" />
              {cap ? `${sold} / ${cap}` : t('card.bookings_short', { count: sold })}
            </span>
          )}
          {cap && !isLiveNow && (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                isSoldOut ? 'bg-red-500/15 text-red-700' : 'bg-secondary text-foreground/70'
              }`}
              data-testid={`live-card-seats-${localLive.id}`}
            >
              {isSoldOut ? t('card.seats_full') : t('card.seats_left', { count: seatsLeft })}
            </span>
          )}
          {isOwner && localLive.privacy === 'private' && localLive.status !== 'ended' && (
            <button
              onClick={copyInviteLink}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-500 transition-colors hover:bg-emerald-500/20"
              data-testid={`live-card-copy-invite-${localLive.id}`}
            >
              <Link2 className="h-3 w-3" /> {t('card.copy_invite')}
            </button>
          )}
        </div>

        {/* CTA — actionable inline buttons, otherwise legacy "Scopri la live" link */}
        {actionable && localLive.status !== 'ended' ? (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            {!user && (
              <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                <LogIn className="h-3 w-3" /> {t('card.login_to_book')}
              </span>
            )}
            <div className="ml-auto">
              <InlineCta
                live={localLive}
                busy={busy}
                alreadyHas={alreadyHas}
                isSoldOut={isSoldOut}
                isLiveNow={isLiveNow}
                onClick={handleAction}
                t={t}
              />
            </div>
          </div>
        ) : !compact && localLive.status !== 'ended' ? (
          <div className="mt-2 flex items-center justify-end">
            <span
              className="inline-flex items-center gap-1 text-xs font-semibold gold-text transition-transform group-hover:translate-x-0.5"
              data-testid={`live-card-cta-${localLive.id}`}
            >
              {t('card.discover_live')} <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        ) : null}
      </div>
    </>
  );

  if (actionable) {
    const onKey = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        goToDetail();
      }
    };
    return (
      <>
      <div
        role="link"
        tabIndex={0}
        onClick={goToDetail}
        onKeyDown={onKey}
        className="group flex w-full cursor-pointer items-stretch gap-3 rounded-xl border border-border bg-card p-3 text-left outline-none transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-amber-400"
        data-testid={`live-card-${localLive.id}`}
      >
        {cardBody}
      </div>
      <InsufficientBalanceDialog
        open={insufficient}
        onOpenChange={setInsufficient}
        balance={Number(user?.wallet_balance || 0)}
        required={Number(localLive.price || 0)}
        context="ticket"
      />
      </>
    );
  }

  return (
    <>
    <div
      role="link"
      tabIndex={0}
      onClick={goToDetail}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToDetail();
        }
      }}
      className="group flex w-full cursor-pointer items-stretch gap-3 rounded-xl border border-border bg-card p-3 text-left outline-none transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-amber-400"
      data-testid={`live-card-${localLive.id}`}
    >
      {cardBody}
    </div>
    <InsufficientBalanceDialog
      open={insufficient}
      onOpenChange={setInsufficient}
      balance={Number(user?.wallet_balance || 0)}
      required={Number(localLive.price || 0)}
      context="ticket"
    />
    </>
  );
};

export default LiveCard;
